import {
  usePausePiiBackfill,
  usePiiBackfillStatus,
  useStartPiiBackfill,
} from "@/api/pii-vault/use-pii-backfill";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Loader2, Pause, Play } from "lucide-react";
import { PiiBackfillProgress } from "./pii-backfill-progress";

const COLLECTION_COUNT = 3;

/**
 * Super-admin control of the vault backfill (RGPD-PII-VAULT-PLAN.md, step 2).
 * Nothing runs until a shop is started here: the vault is filled shop by shop.
 */
export function PiiBackfillCard({ shopId }: { shopId: string }) {
  const status = usePiiBackfillStatus(shopId);
  const start = useStartPiiBackfill(shopId);
  const pause = usePausePiiBackfill(shopId);

  const states = status.data?.states ?? [];
  const isRunning = states.some((state) => !state.isDone && !state.isPaused);
  const isComplete =
    states.length === COLLECTION_COUNT && states.every((state) => state.isDone);
  const isPending = start.isPending || pause.isPending;

  return (
    <Card>
      <CardContent className="py-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-medium">Rattrapage du coffre</h2>
            <p className="text-sm text-muted-foreground">
              Copie les données personnelles des retours, colis et commandes
              déjà en base vers le coffre. Rien n'est supprimé et aucun écran ne
              change de source.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              disabled={isRunning || isComplete || isPending}
              onClick={() =>
                start.mutate({ params: { path: { shopId } }, body: {} })
              }
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {states.length > 0 ? "Reprendre" : "Lancer"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!isRunning || isPending}
              onClick={() => pause.mutate({ params: { path: { shopId } } })}
            >
              <Pause className="w-4 h-4" />
              Pause
            </Button>
          </div>
        </div>

        {isComplete && (
          <p className="text-sm text-emerald-700">
            Rattrapage terminé pour cette boutique.
          </p>
        )}

        {states.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Jamais lancé pour cette boutique.
          </p>
        ) : (
          <div className="space-y-3">
            {states.map((state) => (
              <PiiBackfillProgress key={state.collection} state={state} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
